import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { startRegistration } from "@simplewebauthn/browser";
import { DevicePhoneMobileIcon, FingerPrintIcon, EnvelopeIcon } from "@heroicons/react/24/solid";
import Button from "../common/Button";
import Input from "../common/Input";
import BackupCodesDisplay from "./BackupCodesDisplay";
import { useAuth } from "../../context/AuthContext";
import { useAgencyAuth } from "../../context/AgencyAuthContext";
import Loading from "../common/Loading";
import { toastOptions } from "../common/toastOptions";

type MfaMethodType = "totp" | "email" | "webauthn";
type SetupStep = "choose" | "setup" | "backup" | "done";

// Wrapper that provides the right setMfaVerified based on context
const MFASetup: React.FC<{ isAgency?: boolean }> = ({ isAgency = false }) => {
  return isAgency ? <AgencyMFASetup /> : <UserMFASetup />;
};

const AgencyMFASetup: React.FC = () => {
  const { setMfaVerified } = useAgencyAuth();
  return <MFASetupInner isAgency setMfaVerified={setMfaVerified} />;
};

const UserMFASetup: React.FC = () => {
  const { setMfaVerified } = useAuth();
  return <MFASetupInner isAgency={false} setMfaVerified={setMfaVerified} />;
};

const MFASetupInner: React.FC<{
  isAgency: boolean;
  setMfaVerified: (v: boolean) => void;
}> = ({ isAgency, setMfaVerified }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<SetupStep>("choose");
  const [selectedMethod, setSelectedMethod] = useState<MfaMethodType | null>(
    null
  );
  const [allowedMethods, setAllowedMethods] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // TOTP state
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [manualEntryKey, setManualEntryKey] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [showManualKey, setShowManualKey] = useState(false);

  // WebAuthn state
  const [friendlyName, setFriendlyName] = useState("");
  const [webauthnRegistered, setWebauthnRegistered] = useState(false);

  // Email OTP state
  const [emailChallengeId, setEmailChallengeId] = useState<string | null>(null);
  const [emailCode, setEmailCode] = useState("");

  // Backup codes
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  useEffect(() => {
    fetchMfaStatus();
  }, []);

  const fetchMfaStatus = async () => {
    try {
      const { data } = await axios.get("/api/mfa/status");
      setAllowedMethods(data.allowedMethods || ["totp", "email", "webauthn"]);

      // If only one method allowed, auto-select it
      const methods = data.allowedMethods || ["totp", "email", "webauthn"];
      if (methods.length === 1) {
        setSelectedMethod(methods[0]);
        setStep("setup");
        if (methods[0] === "totp") await startTotpSetup();
      }
    } catch (err) {
      console.error("Error fetching MFA status:", err);
    } finally {
      setLoading(false);
    }
  };

  const startTotpSetup = async () => {
    try {
      setError("");
      const { data } = await axios.post("/api/mfa/totp/setup");
      setQrCodeDataUrl(data.qrCodeDataUrl);
      setManualEntryKey(data.manualEntryKey);
    } catch (err) {
      setError("Failed to start TOTP setup. Please try again.");
    }
  };

  const handleMethodSelect = async (method: MfaMethodType) => {
    setSelectedMethod(method);
    setStep("setup");
    setError("");

    if (method === "totp") {
      await startTotpSetup();
    }
  };

  const handleTotpVerify = async () => {
    try {
      setError("");
      const { data } = await axios.post("/api/mfa/totp/verify-setup", {
        code: totpCode,
      });
      if (data.backupCodes) {
        setBackupCodes(data.backupCodes);
        setStep("backup");
      } else {
        handleSetupComplete();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Invalid code. Please try again.");
    }
  };

  // Email enrollment is send-then-verify: requesting the code proves nothing,
  // so the method is only enabled once the user redeems it.
  const handleEmailSetup = async () => {
    try {
      setError("");
      const { data } = await axios.post("/api/mfa/email/setup");
      setEmailChallengeId(data.challengeId);
      setEmailCode("");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to send verification code.");
    }
  };

  const handleEmailVerify = async () => {
    try {
      setError("");
      const { data } = await axios.post("/api/mfa/email/verify-setup", {
        challengeId: emailChallengeId,
        code: emailCode,
      });
      if (data.backupCodes) {
        setBackupCodes(data.backupCodes);
        setStep("backup");
      } else {
        handleSetupComplete();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Invalid code. Please try again.");
      setEmailCode("");
    }
  };

  const handleWebAuthnSetup = async () => {
    try {
      setError("");

      // Get registration options from server
      const { data: options } = await axios.post(
        "/api/mfa/webauthn/register-options"
      );

      // Trigger browser biometric prompt
      const response = await startRegistration({ optionsJSON: options });

      // Verify with server
      const { data } = await axios.post("/api/mfa/webauthn/register-verify", {
        response,
        friendlyName: friendlyName || "My Device",
      });

      setWebauthnRegistered(true);

      if (data.backupCodes) {
        setBackupCodes(data.backupCodes);
        setStep("backup");
      } else {
        handleSetupComplete();
      }
    } catch (err: any) {
      if (err.name === "NotAllowedError") {
        setError("Registration was cancelled. Please try again.");
      } else {
        setError(
          err.response?.data?.error || "Failed to register device. Please try again."
        );
      }
    }
  };

  const handleSetupComplete = () => {
    setMfaVerified(true);
    window.location.href = isAgency ? "/agency/dashboard" : "/cases";
  };

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen bg-base-200 flex flex-col items-center justify-center p-4">
      <div className="bg-base-100 rounded-xl shadow-sm border border-base-300 p-8 w-full max-w-md">
        <h1 className="text-xl font-semibold text-base-content mb-1 text-center">
          Set Up Multi-Factor Authentication
        </h1>
        <p className="text-sm text-base-content/60 mb-6 text-center">
          Your agency requires MFA for security compliance.
        </p>

        {error && (
          <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 text-sm rounded-lg p-3 mb-4 text-center">
            {error}
          </div>
        )}

        {/* Step 1: Choose method */}
        {step === "choose" && (
          <div className="space-y-3">
            {allowedMethods.includes("totp") && (
              <button
                onClick={() => handleMethodSelect("totp")}
                className="w-full flex items-stretch overflow-hidden border border-base-300 rounded-lg hover:border-blue-300 dark:hover:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/40 transition-colors"
              >
                <div className="shrink-0 w-16 bg-blue-500 flex items-center justify-center">
                  <DevicePhoneMobileIcon className="w-7 h-7 text-white" />
                </div>
                <div className="text-left py-3 px-4">
                  <div className="font-medium text-base-content">
                    Authenticator App
                  </div>
                  <div className="text-xs text-base-content/60 mt-0.5">
                    Use Google Authenticator, Microsoft Authenticator, or similar
                  </div>
                </div>
              </button>
            )}

            {allowedMethods.includes("webauthn") && (
              <button
                onClick={() => handleMethodSelect("webauthn")}
                className="w-full flex items-stretch overflow-hidden border border-base-300 rounded-lg hover:border-blue-300 dark:hover:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/40 transition-colors"
              >
                <div className="shrink-0 w-16 bg-blue-500 flex items-center justify-center">
                  <FingerPrintIcon className="w-7 h-7 text-white" />
                </div>
                <div className="text-left py-3 px-4">
                  <div className="font-medium text-base-content">
                    Device Biometrics
                  </div>
                  <div className="text-xs text-base-content/60 mt-0.5">
                    Use Touch ID, Face ID, Windows Hello, or a security key
                  </div>
                </div>
              </button>
            )}

            {allowedMethods.includes("email") && (
              <button
                onClick={() => handleMethodSelect("email")}
                className="w-full flex items-stretch overflow-hidden border border-base-300 rounded-lg hover:border-blue-300 dark:hover:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/40 transition-colors"
              >
                <div className="shrink-0 w-16 bg-blue-500 flex items-center justify-center">
                  <EnvelopeIcon className="w-7 h-7 text-white" />
                </div>
                <div className="text-left py-3 px-4">
                  <div className="font-medium text-base-content">Email Code</div>
                  <div className="text-xs text-base-content/60 mt-0.5">
                    Receive a verification code via email each time you log in
                  </div>
                </div>
              </button>
            )}
          </div>
        )}

        {/* Step 2: Method-specific setup */}
        {step === "setup" && selectedMethod === "totp" && (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-base-content/70 mb-4">
                Scan this QR code with your authenticator app
              </p>
              {qrCodeDataUrl && (
                <div className="inline-block p-4 bg-white border rounded-lg">{/* QR code frame — stays white in dark mode for scannability */}
                  <img
                    src={qrCodeDataUrl}
                    alt="TOTP QR Code"
                    width={200}
                    height={200}
                  />
                </div>
              )}
            </div>

            <button
              onClick={() => setShowManualKey(!showManualKey)}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300 w-full text-center"
            >
              {showManualKey ? "Hide" : "Can't scan? Enter key manually"}
            </button>

            {showManualKey && (
              <div className="bg-base-200 rounded-lg p-3 text-center">
                <p className="text-xs text-base-content/60 mb-1">Manual entry key:</p>
                <code className="text-sm font-mono break-all">
                  {manualEntryKey}
                </code>
              </div>
            )}

            <div className="text-center">
              <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-1">
                Enter the 6-digit code from your app
              </label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={totpCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setTotpCode(val);
                }}
                placeholder="000000"
                className="text-center text-2xl tracking-widest w-full !max-w-none"
                autoFocus
              />
            </div>

            <Button
              variant="primary"
              onClick={handleTotpVerify}
              disabled={totpCode.length !== 6}
              className="w-full"
            >
              Verify & Continue
            </Button>
          </div>
        )}

        {step === "setup" && selectedMethod === "email" && (
          <div className="space-y-4">
            {!emailChallengeId ? (
              <>
                <div className="text-center py-4">
                  <p className="text-sm text-gray-600 dark:text-base-content/70">
                    A verification code will be emailed to you when you log
                    in. We'll send one now to confirm you can receive it.
                  </p>
                </div>

                <Button
                  variant="primary"
                  onClick={handleEmailSetup}
                  className="w-full"
                >
                  Send Verification Code
                </Button>
              </>
            ) : (
              <>
                <div className="text-center py-4">
                  <p className="text-sm text-gray-600 dark:text-base-content/70">
                    Enter the 6-digit code we just emailed you.
                  </p>
                </div>

                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={emailCode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmailCode(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest w-full !max-w-none"
                  autoFocus
                />

                <Button
                  variant="primary"
                  onClick={handleEmailVerify}
                  disabled={emailCode.length !== 6}
                  className="w-full"
                >
                  Verify &amp; Enable
                </Button>

                <button
                  onClick={handleEmailSetup}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300 w-full text-center"
                >
                  Resend code
                </button>
              </>
            )}
          </div>
        )}

        {step === "setup" && selectedMethod === "webauthn" && (
          <div className="space-y-4">
            {!webauthnRegistered ? (
              <>
                <div className="text-center py-4">
                  <p className="text-sm text-gray-600 dark:text-base-content/70 mb-4">
                    Register your device's biometric (Touch ID, Face ID) or
                    security key for quick, secure authentication.
                  </p>
                </div>

                <div className="text-center">
                  <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-1">
                    Device name (optional)
                  </label>
                  <Input
                    type="text"
                    value={friendlyName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFriendlyName(e.target.value)
                    }
                    placeholder="e.g. Work MacBook"
                    className="w-full !max-w-none text-center"
                  />
                </div>

                <Button
                  variant="primary"
                  onClick={handleWebAuthnSetup}
                  className="w-full"
                >
                  Register This Device
                </Button>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="text-green-600 dark:text-green-400 text-lg mb-2">
                  Device registered successfully!
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Backup codes */}
        {step === "backup" && (
          <div>
            <h2 className="text-lg font-medium text-base-content mb-4 text-center">
              Save Your Backup Codes
            </h2>
            <BackupCodesDisplay
              codes={backupCodes}
              onContinue={handleSetupComplete}
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-col items-center gap-2">
        {step !== "choose" && step !== "backup" && (
          <button
            onClick={() => {
              setStep("choose");
              setSelectedMethod(null);
              setError("");
            }}
            className="text-sm text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
          >
            &larr; Choose a different method
          </button>
        )}
        <button
          onClick={async () => {
            try {
              await axios.post("/api/auth/logout");
            } catch {}
            window.location.href = isAgency ? "/agency/login" : "/login";
          }}
          className="text-sm text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
        >
          Log out
        </button>
      </div>
      <Toaster position="bottom-center" toastOptions={toastOptions} />
    </div>
  );
};

export default MFASetup;
